import type { FunctionComponent } from 'react'
import { useEffect, useMemo, useState } from 'react'
import type { NewsletterSource } from '@which/glide-ts-types'
import { Link, TypographyV2 as Typography } from '@which/seatbelt'
import { dynamicGa4DataLayerPush, removeEmptyProps } from '@which/shared'

import classnames from 'classnames'
import type { DOMNode, HTMLReactParserOptions } from 'html-react-parser'
import parse, { domToReact, Element } from 'html-react-parser'
import DOMPurify from 'isomorphic-dompurify'

import {
  useNewsletterRecommendationsQuery,
  useNewsletterSignupsSalesforceQuery,
} from '../../../generated/frontend'
import { getNewsletterRecommendations } from '../../../pages/signup-complete/components/NewsletterSignups/getMostPopularUnsubscribedNewsletters'
import { newsletterSfNameMap } from '../../../pages/signup-complete/components/NewsletterSignups/newsletterData'
import { useUserEmail } from '../../hooks/useUserEmail'
import { getNewsletterSignupApiUrl } from '../../utils/getNewsletterSignupApiUrl/getNewsletterSignupApiUrl'
import styles from './NewsletterSignUp.module.scss'
import type { NewsletterFormData, SuccessData } from './NewsletterSignUp.types'
import { NewsletterSignUpForm } from './NewsletterSignUpForm'
import { NewsletterSignUpThankYou } from './NewsletterSignUpThankYou'

const errorMessage = 'Failed to subscribe you, please try again later.'

/*
  This is a Glide widget that can be used directly at the Article level only
  For this approach, you need to add & configure for each article individually in Glide
  Known examples:
  -  which.co.uk/news/article/how-to-find-the-best-flexible-flight-deals-afVuF2A1WYwV
  -  which.co.uk/money/mortgages-and-property/mortgage-calculators/stamp-duty-calculator-aFm5J2G7QA5H

  This component is also a child of NewsletterSignUpWithImage WCD component
  NewsletterSignUpWithImage is a Glide widget that can be used at the Collection/Template level
  Known examples:
  -  which.co.uk/topic/test-topic
  -  which.co.uk/money
*/
export const NewsletterSignUp: FunctionComponent<Props> = ({
  className,
  source,
  title,
  subtitle,
  cta,
  privacyPolicy,
  withAllFields,
}) => {
  const { data } = useNewsletterSignupsSalesforceQuery()
  const { data: nlrData } = useNewsletterRecommendationsQuery()
  const email = useUserEmail()
  const isUserAlreadySignedUp = useMemo(() => {
    const currentSubCode = newsletterSfNameMap.get(source)?.code
    const existingSubsCodes = data?.newsletterSignupsSalesforce?.newsletterSignups ?? []
    return currentSubCode ? existingSubsCodes.includes(currentSubCode) : false
  }, [data, source])

  const subNewslettersCodes = useMemo(() => {
    // there is a delay preventing salesforce from returning the recent subscription
    const currentSubCode = newsletterSfNameMap.get(source)?.code
    const existingSubsCodes = data?.newsletterSignupsSalesforce?.newsletterSignups ?? []
    return currentSubCode
      ? Array.from(new Set([currentSubCode, ...existingSubsCodes]))
      : existingSubsCodes
  }, [data, source])

  const [successData, setSuccessData] = useState<SuccessData | null>(null)
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({ newsletterWidget: source } as (typeof window.dataLayer)[number])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isUserAlreadySignedUp && email && !successData) {
      setSuccessData({ email, source })
    }
  }, [isUserAlreadySignedUp, email, successData, source])

  const newsletters = useMemo(
    () =>
      getNewsletterRecommendations({
        subscribed: subNewslettersCodes,
        newsletterRecommendations: nlrData?.newsletterRecommendations.newsletterRecommendations,
        limit: 3,
      }),
    [subNewslettersCodes, nlrData]
  )

  const onSignUp = async (formData: NewsletterFormData) => {
    setLoading(true)

    try {
      const finalData = removeEmptyProps(formData)
      const fetchResponse = await fetch(getNewsletterSignupApiUrl(), {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...finalData,
          newsletterIds: [source === 'tech_support' ? 'TECH' : source.toUpperCase()],
        }),
      })

      setLoading(false)
      if (fetchResponse.ok) {
        const item_text =
          { tech_support: 'tech', food_and_health: 'foodandhealth' }[source] ?? source // this is required because of https://whichonline.atlassian.net/browse/WHC-1055?focusedCommentId=477732 & https://whichonline.atlassian.net/browse/WHC-1069?focusedCommentId=487643
        setSuccessData({ source, ...formData })
        dynamicGa4DataLayerPush({
          event: 'goal_fra_signup',
          utagid: 'WHC561DP01',
          item_text,
          action_group: email ? 'widget - one click signup' : 'widget',
          user_email: formData.email,
          user_postcode: formData.postcode,
        })
      } else {
        setErrorMsg(errorMessage)
      }
    } catch (err) {
      setLoading(false)
      setErrorMsg(errorMessage)
    }
  }

  const parseTextWithLink = (text) => {
    if (!text) {
      return
    }

    const sanitizedHTML = DOMPurify.sanitize(text)
    const options: HTMLReactParserOptions = {
      replace: (domNode: DOMNode) => {
        if (domNode instanceof Element && domNode.name === 'a') {
          const { href } = domNode.attribs

          return (
            <Link href={href} target="_blank" textStyle="sb-text-interface-body-x-small-regular">
              {domToReact(domNode.children as DOMNode[])}
            </Link>
          )
        }
      },
    }

    return (
      <Typography textStyle="sb-text-interface-body-x-small-regular">
        {parse(sanitizedHTML, options)}
      </Typography>
    )
  }

  return (
    <div
      className={classnames(className, styles.newsletterSignUp)}
      data-testid="newsletter-sign-up"
      id={`${source}-newsletter-sign-up`}
    >
      {successData && (
        <NewsletterSignUpThankYou
          successData={successData}
          newsletters={newsletters}
          email={email}
          isUserAlreadySignedUp={isUserAlreadySignedUp}
        />
      )}
      {!successData && (
        <NewsletterSignUpForm
          onSignUp={onSignUp}
          buttonText={cta}
          withAllFields={withAllFields}
          description={subtitle}
          unsubscribeText={parseTextWithLink(privacyPolicy)}
          header={title}
          onFocus={onFocus}
          errorMsg={errorMsg}
          loading={loading}
          email={email}
        />
      )}
    </div>
  )
}

type Props = {
  className?: string
  source: NewsletterSource
  title: string
  subtitle: string
  cta: string
  privacyPolicy?: string
  withAllFields: boolean
}

// onFocus should do nothing because the API no longer requires a cold start hack
const onFocus = () => {}
